import React, { useState, useEffect, useMemo } from 'react'
import { Link, Navigate, useLocation } from 'react-router-dom'
import {
  ClockIcon,
  ShieldCheckIcon,
  ChevronRightIcon,
  ScaleIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline'
import Loader from '../components/Loader'
import HeroAssistant from '../components/home/HeroAssistant'
import FeaturedCarousel from '../components/FeaturedCarousel'
import KbCover from '../components/KbCover'
import { visibleArticles } from '../knowledge/articles'
import { fetchPartnerProducts } from '../api/partnerProducts'
import useDocumentTitle from '../hooks/useDocumentTitle'
import { useAuth } from '../context/AuthContext'
import { useTranslation } from 'react-i18next'
import { jestPolska } from '../kraj'
import StronaGlowna from './StronaGlowna'

/* ============================================================
   KONFIGURACJA, łatwa do aktualizacji (edytuj tylko poniżej)
   ============================================================ */

// Ile produktów partnera pokazujemy w karuzeli i ile artykułów w kaflu Bazy wiedzy.
const PARTNER_PRODUCTS_COUNT = 8
const HOME_ARTICLES_COUNT = 3

// Teksty w słowniku `glowna` (marketing.<id>.tytul / .opis).
const MARKETING = [
  { Icon: ShieldCheckIcon, id: 'ceny' },
  { Icon: ClockIcon, id: 'aktualne' },
  { Icon: ScaleIcon, id: 'porownanie' },
  { Icon: AcademicCapIcon, id: 'pomoc' },
]

/* ============================================================ */

// Strona główna sprzed przebudowy z 2026-10-28; zostaje dla wersji krajowej (RO), gdzie nie ma Rynku ani /dla-firm.
const HomeStara = () => {
  const { t } = useTranslation('glowna')

  const [featured, setFeatured] = useState([])
  const [featLoading, setFeatLoading] = useState(true)

  // Baza wiedzy na stronie głównej: przypięte (homePin) + wyróżniony + najświeższe, razem 3.
  // homePin działa TYLKO tutaj. `featured` steruje też dużym kafelkiem na /baza-wiedzy,
  // więc przypięcie artykułu na stronie głównej nie zmienia tamtej strony.
  const kbArticles = useMemo(() => {
    const all = visibleArticles()
    const pinned = all.filter((a) => a.homePin)
    const featuredKb = all.filter((a) => a.featured && !a.homePin)
    const rest = all
      .filter((a) => !a.featured && !a.homePin)
      .sort((x, y) => String(y.updatedAt || '').localeCompare(String(x.updatedAt || '')))
    return [...pinned, ...featuredKb, ...rest].slice(0, HOME_ARTICLES_COUNT)
  }, [])

  // Karuzela partnera — oferty pobierane PO NAZWIE SPRZEDAWCY, nie po ID ofert
  // (ID zmieniają się przy przescrapowaniu i różnią się między środowiskami).
  useEffect(() => {
    let cancelled = false
    fetchPartnerProducts(PARTNER_PRODUCTS_COUNT)
      .then((items) => { if (!cancelled) setFeatured(items) })
      .catch(() => { if (!cancelled) setFeatured([]) })
      .finally(() => { if (!cancelled) setFeatLoading(false) })
    return () => { cancelled = true }
  }, [])

  return (
    <div className="pb-4">
      {/* ===== HERO = ASYSTENT DOBORU =====
           Asystent jest GŁÓWNĄ funkcją serwisu, a oferty i modele są fundamentem
           pod niego, dlatego zajmuje centralne miejsce, a nie kafelek na dole.
           Logo zostaje w nawigacji; powielanie go w hero zabierało tylko miejsce. */}
      {/* „Sprawdź ofertę” nie ma już paska nad hero: od 2026-09-30 jest pełnym
           kafelkiem w samym hero, obok formularza asystenta (HeroAssistant.jsx). */}
      <HeroAssistant />

      {/* ===== OFERTY PARTNERA / ASYSTENT DOBORU / BAZA WIEDZY ===== */}
      <div className="px-4 md:px-8 lg:px-12 mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
          {/* Karuzela produktów sprzedawcy partnerskiego, pobierane dynamicznie po
              nazwie sprzedawcy (bez listy ID ofert, patrz src/api/partnerProducts.js).
              Zajmuje 3 z 4 kolumn: wyszukiwarka przeniosła się do hero i zwolniła miejsce. */}
          {/* Poza Polską kafla Bazy wiedzy nie ma (artykuły są tylko po polsku),
              więc karuzela zajmuje cały rząd. */}
          <div className={`${jestPolska ? 'lg:col-span-3' : 'lg:col-span-4'} vv-kafel p-5`}>
            <div className="flex items-baseline justify-between gap-3 mb-4">
              <h2 className="text-base font-bold text-gray-900">{t('polecane.tytul')}</h2>
              {/* Bez nazwy jednego sklepu: oferty pochodzą od kilku sprzedawców,
                  a każdy produkt ma własną plakietkę na karcie. */}
              <span className="text-xs text-gray-400 truncate">{t('polecane.podtytul')}</span>
            </div>
            {featLoading ? (
              <div className="flex justify-center items-center py-10"><Loader label="" /></div>
            ) : featured.length > 0 ? (
              <FeaturedCarousel items={featured} maxVisible={4} />
            ) : (
              <p className="text-sm text-gray-400 py-8 text-center">{t('polecane.wkrotce')}</p>
            )}
          </div>

          {/* Baza wiedzy, 3 artykuły z miniaturkami (indeks: src/knowledge/articles.js).
              Tylko w Polsce: artykuły istnieją wyłącznie po polsku. */}
          {jestPolska && (
          <div className="lg:col-span-1 vv-kafel p-5">
            <h2 className="text-base font-bold text-gray-900 mb-4">{t('baza_wiedzy.tytul')}</h2>
            <div className="space-y-3">
              {kbArticles.map((a) => (
                <Link
                  key={a.slug}
                  to={`/baza-wiedzy/${a.slug}`}
                  className="flex items-start gap-3 rounded-xl border border-gray-100 p-3 hover:border-blue-200 hover:bg-blue-50/40 transition-colors group"
                >
                  <KbCover cover={a.cover} variant="thumb" />
                  <span className="min-w-0">
                    <span className="text-sm font-semibold text-gray-800 group-hover:text-blue-700 leading-snug line-clamp-3">{a.title}</span>
                    <span className="mt-1 block text-[11px] text-gray-400">{t('baza_wiedzy.min_czytania', { n: a.readingTime })}</span>
                  </span>
                </Link>
              ))}
              <Link to="/baza-wiedzy" className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:underline pt-1">
                {t('baza_wiedzy.przejdz')} <ChevronRightIcon className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* ===== MARKETING ===== */}
      <div className="px-4 md:px-8 lg:px-12 mt-8">
        <div className="border-t border-gray-100 pt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {MARKETING.map((m) => (
            <div key={m.id} className="flex items-center gap-3">
              <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex-shrink-0">
                <m.Icon className="w-5 h-5" />
              </span>
              <div className="min-w-0">
                <p className="font-semibold text-gray-800 text-sm">{t(`marketing.${m.id}.tytul`)}</p>
                <p className="text-xs text-gray-500">{t(`marketing.${m.id}.opis`)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const Home = () => {
  const { t } = useTranslation('glowna')
  useDocumentTitle(t('tytul_strony'))
  const { firmowe } = useAuth()
  const { key } = useLocation()
  // Konto firmowe startuje na pulpicie także z trwającą sesją (prośba właściciela 2026-11-02). `default` to pierwsze
  // wejście na stronę (adres, zakładka); „Strona główna” z paska (2026-10-30) ma inny klucz i nadal tu prowadzi.
  if (jestPolska && firmowe && key === 'default') return <Navigate to="/pulpit" replace />
  return jestPolska ? <StronaGlowna /> : <HomeStara />
}

export default Home
